import { useContext, useEffect, useRef, useState } from "react";
import VerificationInput from "react-verification-input";
import { Redirect, useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import intl from "react-intl-universal";
import { Countdown } from "../../../../Components/Molecules";
import { MyFirstAccessContext } from "..";
import { FirstAccessShell } from "../FirstAccessShell";
import {
  getFirstAccessErrorMessage,
  isFirstAccessFailure,
} from "../types";
import { FIRST_ACCESS_SKIP_LIVENESS } from "../utils";

/** 2FA step — e-mail OTP after CPF (+ face when liveness required). */
export const ValidateCode = () => {
  const {
    username,
    otpAlreadySent,
    fromPasswordReset,
    sendVerificationCode,
    validateVerificationCode,
    consumeOtpAlreadySent,
  } = useContext(MyFirstAccessContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(!otpAlreadySent);
  const [code, setCode] = useState("");
  const [expired, setExpired] = useState(false);
  const otpSentRef = useRef(false);

  const previousStepPath = fromPasswordReset
    ? "/auth/forgot-password"
    : FIRST_ACCESS_SKIP_LIVENESS
      ? "/auth/my-first-access/confirm-cpf"
      : "/auth/my-first-access/face";

  useEffect(() => {
    if (!username || otpSentRef.current) return;
    otpSentRef.current = true;

    if (otpAlreadySent) {
      consumeOtpAlreadySent();
      setIsBootstrapping(false);
      return;
    }

    (async () => {
      setIsBootstrapping(true);
      const result = await sendVerificationCode(username);
      setIsBootstrapping(false);

      if (isFirstAccessFailure(result)) {
        enqueueSnackbar(
          getFirstAccessErrorMessage(
            result,
            intl.get("firstAccess.sendCodeError")
          ),
          { variant: "error" }
        );
        history.push(previousStepPath);
      }
    })();
  }, [
    consumeOtpAlreadySent,
    enqueueSnackbar,
    history,
    otpAlreadySent,
    previousStepPath,
    sendVerificationCode,
    username,
  ]);

  if (!username) {
    return (
      <Redirect
        to={fromPasswordReset ? "/auth/forgot-password" : "/auth/my-first-access"}
      />
    );
  }

  const resendCode = async () => {
    // Manual "Reenviar" only — Countdown must not auto-call this on timer expiry.
    if (isResending || isBootstrapping) return;
    setIsResending(true);
    try {
      const result = await sendVerificationCode(username);
      if (isFirstAccessFailure(result)) {
        enqueueSnackbar(
          getFirstAccessErrorMessage(result, intl.get("firstAccess.sendCodeError")),
          { variant: "error" }
        );
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async () => {
    if (expired || isLoading || isResending || code.length !== 6) return;

    setIsLoading(true);
    const result = await validateVerificationCode(username, code);
    setIsLoading(false);

    if (isFirstAccessFailure(result)) {
      enqueueSnackbar(
        getFirstAccessErrorMessage(
          result,
          intl.get("firstAccess.validateCodeError")
        ),
        { variant: "error" }
      );
      return;
    }

    history.push("/auth/my-first-access/change-password");
  };

  return (
    <FirstAccessShell
      title={intl.get("firstAccess.verifyTitle")}
      subtitle={intl.get("firstAccess.verifySubtitle")}
      variant="verification"
    >
      {isBootstrapping ? (
        <div className="flex min-h-[120px] flex-col items-center justify-center py-8">
          <span className="login-submit-btn__spinner" />
          <p className="mt-3 text-sm">{intl.get("firstAccess.sendingCode")}</p>
        </div>
      ) : (
        <form
          className="login-verification-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (expired || isLoading || isResending || code.length !== 6) return;
            handleSubmit();
          }}
        >
          <div className="login-verification-otp">
            <VerificationInput
              length={6}
              value={code}
              onChange={setCode}
              placeholder=""
              autoFocus
              validChars="0-9"
              classNames={{
                container: "login-verification-otp__container",
                character: "login-verification-otp__char",
                characterInactive: "login-verification-otp__char--inactive",
                characterSelected: "login-verification-otp__char--selected",
              }}
              inputProps={
                {
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    event.stopPropagation();
                    if (expired || isLoading || isResending || code.length !== 6) {
                      return;
                    }
                    handleSubmit();
                  },
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
            />
          </div>

          <div className="login-verification-countdown">
            <Countdown
              expireTime={60}
              resendCode={resendCode}
              isExpired={setExpired}
              resendLabel={intl.get("auth.resendEmail")}
            />
          </div>

          <div className="login-verification-actions">
            <button
              type="button"
              className="login-verification-btn login-verification-btn--outline"
              onClick={() => history.push(previousStepPath)}
              disabled={isLoading || isResending}
            >
              {intl.get("words.back")}
            </button>
            <button
              type="submit"
              className="login-verification-btn login-submit-btn"
              disabled={expired || code.length !== 6 || isLoading || isResending}
            >
              {isLoading ? (
                <>
                  <span className="login-submit-btn__spinner" />
                  {intl.get("auth.verifying")}
                </>
              ) : (
                intl.get("auth.verify")
              )}
            </button>
          </div>
        </form>
      )}
    </FirstAccessShell>
  );
};
